import React, {useState, useContext, Fragment, memo} from "react";
import moment from "moment";
import __ from "lodash";
import {FormControl, Icon, IconButton, InputAdornment, InputLabel, makeStyles, MenuItem, Select, TextField, Tooltip, Typography} from '@material-ui/core';
import {HighlightOff, Search} from '@material-ui/icons';
import SystemContext from "../SystemContext";
import {ActionsList, CheckboxEditor, ComboSelect, DatePicker, MaskedNumber, MaskedText} from "../controls";
import BaseListStyle from "../modules/styles/BaseListStyle";
import { isBlank, useStateCallback } from "../utils";

const dateFormat = "DD/MM/YYYY";
const marginLeft = 10;

const SearchBarTheme = (theme) => {
  const baseListStyle = BaseListStyle(theme);
  return {
    ...baseListStyle,
    selectWithClear: {
      color: "red",
      "& .MuiSelect-icon": {
        right: "12px"
      }
    }
  }
}

const SearchBar = (props) => {
  const SystemData = useContext(SystemContext);
  const eventBus = SystemData.eventBus;
  const classes = makeStyles(SearchBarTheme)();

  const {fields} = props;

  const emptySearch = {};  

  fields.forEach(field => { 
    const fieldName = field.field || field.fieldName;
    let defaultValue = field.defaultValue===undefined ? null : field.defaultValue; 
    emptySearch[fieldName] = defaultValue; 
  });

  const [search, setSearch] = useStateCallback({
    ...emptySearch,
  });
  const [oldProps, setOldProps] = useState({
    ...props,
  });

  if (JSON.stringify(oldProps.fields) !== JSON.stringify(props.fields)) {
    setOldProps({...props});
    setSearch({...emptySearch, ...search});
  }

  const checkSearchDisabled = () => {
    let searchDisabled = false;
    if (typeof props.searchDisabled === 'function') {
      searchDisabled = props.searchDisabled(search);
    } 
    if (typeof props.searchDisabled === 'boolean') {
      searchDisabled = props.searchDisabled;
    } 
    return searchDisabled;
  }

  const handleSearch = () => {
    if (typeof props.onSearch === 'function') {
      props.onSearch(search, recordChange);
    } 
    if (eventBus) {
      eventBus.trigger(`${props.eventPrefix}.search`, {...search});
    }
  }
  
  const handleClearSelections = () => {
    setSearch(emptySearch);
    if (typeof props.onResetSearch === 'function') {
      props.onResetSearch(emptySearch, recordChange);
    }  
    if (eventBus) {      
      eventBus.trigger(`${props.eventPrefix}.resetSearch`);
    }
  }

  const recordChange = (changes) => {
    setSearch({...search, ...changes}, ()=> {
      if (typeof props.onSearchChanged === 'function') {
        props.onSearchChanged(search, recordChange);
      } 
      if (eventBus) {
        eventBus.trigger(`${props.eventPrefix}.searchChanged`, {...search});
      }
    });
  }

  const renderFields = () => {
    return fields.map(field => { 
      const fieldName = field.field || field.fieldName; 
      const fieldProps = {...__.omit(field, ['onChange', 'label', 'width'])};
      const width = field.width?{width: field.width}:{};
      const style={...field.style, ...width}    
      const containerStyle={...field.containerStyle, ...width}  

      const onChange = (newValue) => {
        const change = {};
        change[fieldName] = newValue;
        setSearch({...search, ...change}, ()=> {
          if (typeof props.onSearchChanged === 'function') {
            props.onSearchChanged(search, recordChange);
          } 
        });
      }
      const handleClearSearch=()=>{
        onChange(null);        
      }
      const clearSearch = {endAdornment: <IconButton style={{padding:0}} disabled={isBlank(search[fieldName])} onClick={handleClearSearch}><Icon fontSize="small">clear</Icon></IconButton>}
      if (typeof field.render === 'function') {
        const renderedField = field.render(search, {...field, value: search[fieldName], clearSearch, shrinkAll: props.shrinkAll}, onChange, recordChange);
        if (field.addWrapper) {
          return (
            <FormControl variant="standard" sx={{ m: 1}} style={{marginLeft: marginLeft}}>
              {renderedField}
            </FormControl>
          )  
        }
        return renderedField;
      }
      let response = (<Fragment>Unable to Render field</Fragment>);
      switch (field.type) {
        case "date":
          response = (
            <DatePicker
              key={fieldName}
              label={field.label || fieldName}
              style={{width: 130, marginLeft: marginLeft, ...style}}
              value={search[fieldName]}
              defaultValue={null}
              disableToolbar={true}
              autoOk={true}
              format={dateFormat}
              {...fieldProps}
              returnInvalidDate
              onChange={(value)=> {
                const newDate = moment(value).toISOString();
                onChange(newDate);
              }}              
            />
          );
          break; 
        case "checkbox":
          response = (
            <CheckboxEditor
              id={`searchbar_checkbox_${fieldName}`}
              key={fieldName}
              value={search[fieldName]}
              checked={search[fieldName]}
              style={field.labelPosition==="start"?{marginRight:marginLeft, textAlign: "left", ...style}:{marginLeft:marginLeft, textAlign: "left", ...style}}                
              {...fieldProps}
              onChange={(checked)=> {
                onChange(checked);
              }}
              label={field.label || fieldName}
              // labelPosition={field.labelPosition}
            />
          );
          break; 
        case "select":
          response = (
            <FormControl variant="standard" sx={{ m: 1}} style={{marginLeft: marginLeft, textAlign: "left"}}>
              <TextField
                className={classes.selectWithClear}
                select
                style={{width: 300, ...style}}
                key={fieldName}
                id={`searchbar_select_${fieldName}`}
                value={search[fieldName]||''}
                fullWidth
                InputProps={{...clearSearch}}
                InputLabelProps={{shrink: field.shrink||props.shrinkAll||!__.isEmpty(search[fieldName])}}
                onChange={(event)=> {
                  onChange(event.target.value);
                }}
                label={field.label || fieldName}
              >
                {
                  (field.options||[]).map((item, i) => {
                    if (typeof item === "string") {
                      return (<MenuItem key={item} value={item} style={{}}>{item}</MenuItem>);
                    }
                    if (typeof item === "object") {
                      return (<MenuItem key={item.key||item.value||`${fieldName}_option_${i}`} value={item.value}>{item.text || item.value}</MenuItem>);
                    }
                    return null;
                  })
                }
              </TextField>
            </FormControl>  
          );
          break; 
        case "combo":
          response = (
            <ComboSelect
              id={`searchbar_combo_${fieldName}`}
              label={field.label || fieldName}
              key={fieldName}
              className={classes.selectWithClear}
              containerStyle={{marginLeft: marginLeft, width: 300, ...containerStyle}}
              style={{textAlign: 'left', ...style}}
              fullWidth={true}
              group={field.group}
              subGroup={field.subGroup||''}
              value={__.isEmpty(search[fieldName])?"":search[fieldName]}
              inputProps={clearSearch}
              inputLabelProps={{shrink: field.shrink||props.shrinkAll||!__.isEmpty(search[fieldName])}}
              {...fieldProps}
              onChange={(newValue)=> {
                onChange(newValue);
              }}
            />
          );
          break; 
        case "number":
          response = (
            <MaskedNumber
              id={`searchbar_number_${fieldName}`}
              key={fieldName}
              label={field.label || fieldName}
              containerStyle={{width: 300, marginLeft: marginLeft, ...containerStyle}}
              style={{...style}}
              fullWidth
              value={search[fieldName]}
              InputProps={clearSearch}
              thousandSeparator={false}
              inputLabelProps={{shrink: field.shrink||props.shrinkAll||!__.isEmpty(search[fieldName])}}
              {...__.omit(fieldProps, ['type', 'label', 'id', 'key', 'shrink', 'shrinkAll'])}
              onChange={(newValue)=> {
                onChange(newValue);
              }}
              customInput={TextField}
            />
          );
          break; 
        default: 
          response = (
            <MaskedText
              id={`searchbar_text_${fieldName}`}
              key={fieldName}
              label={field.label || fieldName}
              style={{width: 300, marginTop: -1, marginLeft: marginLeft, ...style}}
              mask={"A".repeat(200)}
              forceUpperCase={true}
              inputLabelProps={{ shrink: field.shrink||props.shrinkAll }}
              fullwidth
              fields={search}
              value={search[fieldName]||""}
              defaultValue=""
              InputProps={clearSearch}
              {...fieldProps}
              onChange={(newValue)=> {
                onChange(newValue);
              }}
            />    
          )
        break;
      }
      return response;
    });
  }

  const renderSearchButtons = () => {
    const searchDisabled = checkSearchDisabled();
    return (
      <>
        {props.hideSearchButtons?null:
          <span style={{width: 5, minWidth: 5}}/>      
        }
        {props.hideSearchButtons?null:
          <IconButton style={{padding: "20px 5px", ...props.searchButtonStyle}} disabled={searchDisabled} onClick={()=>{handleSearch()} }>
            <Tooltip title={props.searchButtonTooltip||"Click to search"}>
              <Search style={{...props.searchButtonIconStyle}} color={searchDisabled?"#909090":"primary"}/>
            </Tooltip>
          </IconButton>
        }
        {props.hideSearchButtons?null:
          <IconButton style={{padding: "20px 5px", ...props.clearButtonStyle}} onClick={()=>{handleClearSelections()}}>
            <Tooltip title={props.clearButtonTooltip||"Click to clear all search fields"}>
              <HighlightOff style={{...props.clearButtonIconStyle}} color="primary"/>
            </Tooltip>
          </IconButton>          
        }
      </>
    )
  }

  return (
    <div className={classes.rooted} style={{width: "100%", height: 70, display: "block"}}>
      <div className={classes.rooted} style={{height: "100%", display: "block", marginLeft: 40, marginRight: 40, backgroundColor: "#ffffff", color: "rgba(0, 0, 0, 0.87)"}}>
        <div style={{padding: "5px 20px 10px 20px", alignItems: "center",  justifyContent: "center",  width: "calc(100% - 30px)", display: "flex", ...props.boxStyle}}>
          {props.heading?
            <Typography 
              variant="h6" 
              component="h6" 
              style={{paddingTop: 12, paddingRight: 20, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}
              {...props.headingProps}
            >
              {props.heading}
            </Typography>
          :null}
          <div>
          {props.alignFields === "right"?null:renderFields()}
          {props.alignFields === "right"?null:renderSearchButtons()}            
          </div>
          <div style={{ display: "flex", marginLeft: "auto", marginRight: "0"}}>
            {props.alignFields === "right"?renderFields():null}
            {props.alignFields === "right"?renderSearchButtons():null}
            <ActionsList
              id={"action-list"}
              style={{padding: 0}}
              actions={props.actions||[]}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default memo(SearchBar);
